/**
 * Folder sidebar - displays folder list with counts and actions
 */
import clsx from 'clsx';
import { useAtom } from 'jotai';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IoAdd,
  IoArrowDown,
  IoArrowUp,
  IoCheckmark,
  IoChevronBack,
  IoChevronDown,
  IoChevronForward,
  IoEllipsisVertical,
  IoFolderOpen,
  IoFolderOpenOutline,
  IoLayers,
  IoPencil,
  IoReorderThree,
  IoSwapVertical,
  IoTrash,
} from 'react-icons/io5';

import { closestCenter, DndContext, DragOverlay, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import Spinner from '@/components/Spinner/Spinner';
import { calculateDropdownPosition } from '@/hooks/useDropdownPosition';
import { getContrastColor } from '@/lib/color';
import type { EnrichedCollectionCard, Folder } from '@/types/card';
import type { FolderSortMode } from '../atoms';
import { folderSortModeAtom } from '../atoms';
import { useDeleteFolderMutation, useFoldersQuery, useReorderFoldersMutation } from '../hooks/api';
import { sortSiblings } from '../lib/folderTree';
import FolderModal from './FolderModal';

const SORT_OPTIONS: { value: FolderSortMode; label: string }[] = [
  { value: 'manual', label: 'Manual' },
  { value: 'name', label: 'Name' },
  { value: 'cardCount', label: 'Card Count' },
  { value: 'updatedAt', label: 'Updated' },
];

/** Thin wrapper that applies useSortable to a folder row */
function SortableFolder({
  id,
  disabled,
  children,
}: {
  id: string;
  disabled: boolean;
  children: (props: {
    isDragging: boolean;
    dragHandleProps: Record<string, unknown> | undefined;
    style: React.CSSProperties;
  }) => ReactElement;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  };
  const dragHandleProps = disabled ? undefined : { ...attributes, ...listeners };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ isDragging, dragHandleProps, style: {} })}
    </div>
  );
}

export type SidebarWidth = 'collapsed' | 'normal';

export interface FolderSidebarProps {
  cards: EnrichedCollectionCard[];
  selectedFolderId: string | null | undefined;
  onSelectFolder: (folderId: string | null | undefined) => void;
  width: SidebarWidth;
  onCollapse: () => void;
  onExpand: () => void;
}

export default function FolderSidebar({
  cards,
  selectedFolderId,
  onSelectFolder,
  width,
  onCollapse,
  onExpand,
}: FolderSidebarProps): ReactElement {
  const { data: folders, isLoading } = useFoldersQuery();
  const deleteMutation = useDeleteFolderMutation();
  const reorderMutation = useReorderFoldersMutation();
  const [folderSortMode, setFolderSortMode] = useAtom(folderSortModeAtom);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [createSubfolderParentId, setCreateSubfolderParentId] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [sortMenuPosition, setSortMenuPosition] = useState({ top: 0, left: 0 });

  const isCollapsed = width === 'collapsed';

  // Calculate card counts per folder (direct count)
  const folderDirectCounts = new Map<string | null, number>();
  cards.forEach((card) => {
    const folderId = card.folder_id ?? null;
    folderDirectCounts.set(folderId, (folderDirectCounts.get(folderId) ?? 0) + card.quantity);
  });

  const totalCards = cards.reduce((sum, c) => sum + c.quantity, 0);
  const unfolderedCount = folderDirectCounts.get(null) ?? 0;

  // Calculate recursive counts (folder + all descendants)
  const folderTotalCounts = useMemo(() => {
    const totals = new Map<string, number>();
    if (!folders) return totals;

    const calculateTotal = (folderId: string): number => {
      if (totals.has(folderId)) return totals.get(folderId)!;

      const directCount = folderDirectCounts.get(folderId) ?? 0;
      const children = folders.filter((f) => f.parent_id === folderId);
      const childrenTotal = children.reduce((sum, child) => sum + calculateTotal(child.id), 0);
      const total = directCount + childrenTotal;

      totals.set(folderId, total);
      return total;
    };

    folders.forEach((f) => calculateTotal(f.id));
    return totals;
  }, [folders, folderDirectCounts]);

  // Build folder tree (sorted)
  const rootFolders = useMemo(
    () => sortSiblings(folders?.filter((f) => !f.parent_id) ?? [], folderSortMode, folderTotalCounts),
    [folders, folderSortMode, folderTotalCounts],
  );
  const getChildFolders = (parentId: string) =>
    sortSiblings(folders?.filter((f) => f.parent_id === parentId) ?? [], folderSortMode, folderTotalCounts);

  const toggleExpanded = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleEdit = (folder: Folder) => {
    setEditingFolder(folder);
    setIsModalOpen(true);
    setMenuOpenFor(null);
  };

  const handleDelete = async (folderId: string) => {
    if (!confirm('Delete this folder? Cards will be moved to "Unfiled".')) {
      return;
    }
    await deleteMutation.mutateAsync(folderId);
    if (selectedFolderId === folderId) {
      onSelectFolder(null);
    }
    setMenuOpenFor(null);
  };

  const handleCreateNew = () => {
    setEditingFolder(null);
    setCreateSubfolderParentId(null);
    setIsModalOpen(true);
  };

  const handleCreateSubfolder = (parentId: string) => {
    setEditingFolder(null);
    setCreateSubfolderParentId(parentId);
    setIsModalOpen(true);
    setMenuOpenFor(null);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingFolder(null);
    setCreateSubfolderParentId(null);
  };

  const handleMoveFolder = (folderId: string, direction: 'up' | 'down') => {
    if (!folders) return;
    const folder = folders.find((f) => f.id === folderId);
    if (!folder) return;
    const siblings = sortSiblings(
      folders.filter((f) => (f.parent_id ?? null) === (folder.parent_id ?? null)),
      'manual',
      folderTotalCounts,
    );
    const idx = siblings.findIndex((f) => f.id === folderId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;
    const reordered = [...siblings];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    reorderMutation.mutate(reordered.map((f) => f.id));
    setMenuOpenFor(null);
  };

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id || !folders) return;

      const draggedFolder = folders.find((f) => f.id === active.id);
      if (!draggedFolder) return;

      // Find siblings of the dragged folder
      const parentId = draggedFolder.parent_id ?? null;
      const siblings = sortSiblings(
        folders.filter((f) => (f.parent_id ?? null) === parentId),
        'manual',
        folderTotalCounts,
      );
      const oldIndex = siblings.findIndex((f) => f.id === active.id);
      const newIndex = siblings.findIndex((f) => f.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;

      const reordered = arrayMove(siblings, oldIndex, newIndex);
      reorderMutation.mutate(reordered.map((f) => f.id));
    },
    [folders, folderTotalCounts, reorderMutation],
  );

  const handleDragCancel = useCallback(() => {
    setActiveDragId(null);
  }, []);

  const activeDragFolder = activeDragId ? folders?.find((f) => f.id === activeDragId) : null;

  const isDndEnabled = folderSortMode === 'manual';

  const renderFolderContent = (folder: Folder, depth: number, dragHandleProps?: Record<string, unknown>) => {
    const children = getChildFolders(folder.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedFolders.has(folder.id);
    const isSelected = selectedFolderId === folder.id;
    const directCount = folderDirectCounts.get(folder.id) ?? 0;
    const totalCount = folderTotalCounts.get(folder.id) ?? 0;
    const showTotalCount = hasChildren && totalCount > directCount;
    const isMenuOpen = menuOpenFor === folder.id;

    // Compute sibling position for move up/down
    const siblings = folders
      ? sortSiblings(
          folders.filter((f) => (f.parent_id ?? null) === (folder.parent_id ?? null)),
          'manual',
          folderTotalCounts,
        )
      : [];
    const siblingIdx = siblings.findIndex((f) => f.id === folder.id);
    const isFirst = siblingIdx <= 0;
    const isLast = siblingIdx >= siblings.length - 1;

    return (
      <>
        <div
          className={clsx(
            'group relative flex items-center rounded-lg transition-colors',
            isCollapsed ? 'justify-center px-2' : '',
            isSelected ? 'bg-aether/20 text-pearl' : 'text-silver hover:bg-slate hover:text-pearl',
          )}
          style={{ paddingLeft: isCollapsed ? undefined : `${depth * 12 + 8}px` }}
        >
          {/* Drag handle */}
          {!isCollapsed && isDndEnabled && dragHandleProps && (
            <button
              className="text-ash hover:text-pearl flex h-7 w-5 shrink-0 cursor-grab items-center justify-center opacity-0 group-hover:opacity-100"
              aria-label="Drag to reorder"
              {...dragHandleProps}
            >
              <IoReorderThree className="h-4 w-4" />
            </button>
          )}

          {/* Expand/collapse toggle */}
          {!isCollapsed && hasChildren ? (
            <button
              onClick={() => toggleExpanded(folder.id)}
              className="text-ash hover:text-pearl flex h-7 w-7 shrink-0 items-center justify-center"
              aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
            >
              {isExpanded ? <IoChevronDown className="h-3.5 w-3.5" /> : <IoChevronForward className="h-3.5 w-3.5" />}
            </button>
          ) : !isCollapsed ? (
            <div className="w-7 shrink-0" />
          ) : null}

          {/* Folder button */}
          <button
            onClick={() => onSelectFolder(folder.id)}
            onKeyDown={(e) => {
              if (folderSortMode === 'manual' && e.altKey) {
                if (e.key === 'ArrowUp' && !isFirst) {
                  e.preventDefault();
                  handleMoveFolder(folder.id, 'up');
                } else if (e.key === 'ArrowDown' && !isLast) {
                  e.preventDefault();
                  handleMoveFolder(folder.id, 'down');
                }
              }
            }}
            className={clsx('flex min-w-0 flex-1 items-center py-2 text-left', isCollapsed ? 'justify-center' : 'gap-2 pr-2')}
            title={folder.name}
          >
            {isCollapsed ? (
              <span className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center">
                <IoFolderOpen className="h-4 w-4" style={{ color: folder.color ?? undefined }} />
                <span
                  className="absolute leading-none font-bold"
                  style={{
                    fontSize: '9px',
                    color: folder.color ? getContrastColor(folder.color) : 'black',
                    transform: 'translateY(2.5px)',
                  }}
                >
                  {folder.name.charAt(0).toUpperCase()}
                </span>
              </span>
            ) : (
              <IoFolderOpen className="h-4 w-4 shrink-0" style={{ color: folder.color ?? undefined }} />
            )}
            {!isCollapsed && <span className="truncate text-sm">{folder.name}</span>}
            {!isCollapsed && (
              <span className="text-ash ml-auto shrink-0 text-xs">
                {showTotalCount ? (
                  <>
                    <span className="text-silver">{totalCount}</span>
                    <span className="text-ash/60 mx-0.5">·</span>
                    <span>{directCount}</span>
                  </>
                ) : (
                  directCount
                )}
              </span>
            )}
          </button>

          {/* Menu toggle */}
          {!isCollapsed && (
            <div className="relative">
              <button
                onClick={(e) => {
                  if (isMenuOpen) {
                    setMenuOpenFor(null);
                  } else {
                    const pos = calculateDropdownPosition(e.currentTarget, {
                      placement: 'bottom-end',
                      estimatedWidth: 160,
                      estimatedHeight: folderSortMode === 'manual' ? 280 : 140,
                    });
                    setMenuPosition({ top: pos.top, left: pos.left });
                    setMenuOpenFor(folder.id);
                  }
                }}
                className={clsx(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors',
                  isMenuOpen ? 'bg-storm text-pearl' : 'text-ash hover:bg-storm hover:text-pearl opacity-0 group-hover:opacity-100',
                )}
                aria-label="Folder options"
              >
                <IoEllipsisVertical className="h-4 w-4" />
              </button>

              {/* Dropdown menu - rendered via portal */}
              {isMenuOpen &&
                createPortal(
                  <>
                    <div className="fixed inset-0 z-50" onClick={() => setMenuOpenFor(null)} />
                    <div
                      className="bg-obsidian border-storm fixed z-50 w-40 overflow-hidden rounded-lg border py-1 shadow-xl"
                      style={{ top: menuPosition.top, left: menuPosition.left }}
                    >
                      <button
                        onClick={() => handleCreateSubfolder(folder.id)}
                        className="text-silver hover:bg-slate hover:text-pearl flex w-full items-center gap-2 px-3 py-2 text-sm"
                      >
                        <IoFolderOpenOutline className="h-4 w-4" />
                        New sub-folder
                      </button>
                      <button
                        onClick={() => handleEdit(folder)}
                        className="text-silver hover:bg-slate hover:text-pearl flex w-full items-center gap-2 px-3 py-2 text-sm"
                      >
                        <IoPencil className="h-4 w-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(folder.id)}
                        className="text-danger hover:bg-danger/10 flex w-full items-center gap-2 px-3 py-2 text-sm"
                      >
                        <IoTrash className="h-4 w-4" />
                        Delete
                      </button>
                      {folderSortMode === 'manual' && (
                        <>
                          <div className="border-storm my-1 border-t" />
                          <button
                            onClick={() => handleMoveFolder(folder.id, 'up')}
                            disabled={isFirst}
                            className={clsx(
                              'flex w-full items-center gap-2 px-3 py-2 text-sm',
                              isFirst ? 'text-ash cursor-not-allowed' : 'text-silver hover:bg-slate hover:text-pearl',
                            )}
                          >
                            <IoArrowUp className="h-4 w-4" />
                            Move Up
                          </button>
                          <button
                            onClick={() => handleMoveFolder(folder.id, 'down')}
                            disabled={isLast}
                            className={clsx(
                              'flex w-full items-center gap-2 px-3 py-2 text-sm',
                              isLast ? 'text-ash cursor-not-allowed' : 'text-silver hover:bg-slate hover:text-pearl',
                            )}
                          >
                            <IoArrowDown className="h-4 w-4" />
                            Move Down
                          </button>
                        </>
                      )}
                    </div>
                  </>,
                  document.body,
                )}
            </div>
          )}
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <SortableContext items={children.map((c) => c.id)} strategy={verticalListSortingStrategy} disabled={!isDndEnabled}>
            <div>{children.map((child) => renderFolder(child, depth + 1))}</div>
          </SortableContext>
        )}
      </>
    );
  };

  const renderFolder = (folder: Folder, depth: number = 0): ReactElement => {
    if (isDndEnabled) {
      return (
        <SortableFolder key={folder.id} id={folder.id} disabled={false}>
          {({ dragHandleProps }) => renderFolderContent(folder, depth, dragHandleProps)}
        </SortableFolder>
      );
    }
    return <div key={folder.id}>{renderFolderContent(folder, depth)}</div>;
  };

  const widthClass = width === 'collapsed' ? 'w-14' : 'w-96';

  return (
    <aside className={clsx('bg-obsidian border-storm flex shrink-0 flex-col border-r transition-all duration-200', widthClass)}>
      {/* Header */}
      <div className={clsx('border-storm flex items-center border-b py-3', isCollapsed ? 'justify-center px-2' : 'gap-2 px-3')}>
        {/* Collapse/Expand button */}
        {isCollapsed ? (
          <button
            onClick={onExpand}
            className="text-ash hover:bg-slate hover:text-pearl flex h-7 w-7 items-center justify-center rounded-md transition-colors"
            aria-label="Expand sidebar"
            title="Expand sidebar"
          >
            <IoChevronForward className="h-5 w-5" />
          </button>
        ) : (
          <>
            <button
              onClick={onCollapse}
              className="text-ash hover:bg-slate hover:text-pearl flex h-7 w-7 items-center justify-center rounded-md transition-colors"
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
            >
              <IoChevronBack className="h-5 w-5" />
            </button>

            <h2 className="font-heading text-pearl flex-1 text-sm font-semibold tracking-wider uppercase">Folders</h2>

            {/* Sort mode button */}
            <div className="relative">
              <button
                onClick={(e) => {
                  if (!isSortMenuOpen) {
                    const pos = calculateDropdownPosition(e.currentTarget, {
                      placement: 'bottom-end',
                      estimatedWidth: 144,
                      estimatedHeight: 160,
                    });
                    setSortMenuPosition({ top: pos.top, left: pos.left });
                  }
                  setIsSortMenuOpen((prev) => !prev);
                }}
                className={clsx(
                  'flex h-7 w-7 items-center justify-center rounded-md transition-colors',
                  isSortMenuOpen ? 'bg-storm text-pearl' : 'text-ash hover:bg-slate hover:text-pearl',
                )}
                aria-label="Sort folders"
                title={`Sort: ${SORT_OPTIONS.find((o) => o.value === folderSortMode)?.label}`}
              >
                <IoSwapVertical className="h-4 w-4" />
              </button>
              {isSortMenuOpen &&
                createPortal(
                  <>
                    <div className="fixed inset-0 z-50" onClick={() => setIsSortMenuOpen(false)} />
                    <div
                      className="bg-obsidian border-storm fixed z-50 w-36 overflow-hidden rounded-lg border py-1 shadow-xl"
                      style={{ top: sortMenuPosition.top, left: sortMenuPosition.left }}
                    >
                      {SORT_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => {
                            setFolderSortMode(opt.value);
                            setIsSortMenuOpen(false);
                          }}
                          className={clsx(
                            'flex w-full items-center gap-2 px-3 py-2 text-sm',
                            folderSortMode === opt.value ? 'text-pearl' : 'text-silver hover:bg-slate hover:text-pearl',
                          )}
                        >
                          {folderSortMode === opt.value ? <IoCheckmark className="h-4 w-4 shrink-0" /> : <span className="w-4 shrink-0" />}
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </>,
                  document.body,
                )}
            </div>

            {/* Create folder button */}
            <button
              onClick={handleCreateNew}
              className="text-ash hover:bg-slate hover:text-pearl flex h-7 w-7 items-center justify-center rounded-md transition-colors"
              aria-label="Create folder"
              title="Create folder"
            >
              <IoAdd className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {/* Folder list */}
      <nav className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner size="sm" />
          </div>
        ) : (
          <div className="space-y-0.5">
            {/* All cards */}
            <button
              onClick={() => onSelectFolder(undefined)}
              className={clsx(
                'flex w-full items-center rounded-lg py-2 text-left transition-colors',
                isCollapsed ? 'justify-center px-2' : 'gap-2 px-3',
                selectedFolderId === undefined ? 'bg-aether/20 text-pearl' : 'text-silver hover:bg-slate hover:text-pearl',
              )}
              title="All Cards"
            >
              <IoLayers className="h-4 w-4 shrink-0" />
              {!isCollapsed && <span className="text-sm">All Cards</span>}
              {!isCollapsed && <span className="text-ash ml-auto text-xs">{totalCards}</span>}
            </button>

            {/* Unfiled */}
            <button
              onClick={() => onSelectFolder(null)}
              className={clsx(
                'flex w-full items-center rounded-lg py-2 text-left transition-colors',
                isCollapsed ? 'justify-center px-2' : 'gap-2 px-3',
                selectedFolderId === null ? 'bg-aether/20 text-pearl' : 'text-silver hover:bg-slate hover:text-pearl',
              )}
              title="Unfiled"
            >
              <IoFolderOpen className="text-ash h-4 w-4 shrink-0" />
              {!isCollapsed && <span className="text-sm">Unfiled</span>}
              {!isCollapsed && <span className="text-ash ml-auto text-xs">{unfolderedCount}</span>}
            </button>

            {/* Divider */}
            {rootFolders.length > 0 && <div className="border-storm my-2 border-t" />}

            {/* User folders */}
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext items={rootFolders.map((f) => f.id)} strategy={verticalListSortingStrategy} disabled={!isDndEnabled}>
                {rootFolders.map((folder) => renderFolder(folder))}
              </SortableContext>
              <DragOverlay>
                {activeDragFolder ? (
                  <div className="bg-obsidian/90 border-storm flex items-center gap-2 rounded-lg border px-3 py-2 shadow-lg">
                    <IoFolderOpen className="h-4 w-4 shrink-0" style={{ color: activeDragFolder.color ?? undefined }} />
                    <span className="text-pearl truncate text-sm">{activeDragFolder.name}</span>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        )}
      </nav>

      {/* Create folder modal */}
      <FolderModal isOpen={isModalOpen} onClose={handleModalClose} folder={editingFolder} defaultParentId={createSubfolderParentId} />
    </aside>
  );
}
